import { FC, useState } from 'react';
import { useQueryClient } from 'react-query';
import { PersonsV3 } from '@frontend/api-person';
import { TaskCenterQueries, TaskCenterTypes, TaskCenterUtils, useTaskCenterMutations } from '@frontend/api-task-center';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { PanelHeader } from '@frontend/panel-engine';
import { CitationMetadata, taskEvents, useDeleteTask, useTaskCenterStore } from '@frontend/task-center-scope';
import { Button, ButtonBar, useAlert } from '@frontend/design-system';
import { TabsController } from '../../constants';
import { useAssigneeByLocation, useGetFormDetails, useRemoveQueryByKey, useTaskFormPatient } from '../../hooks';
import { taskCenterTrackingIds } from '../../tracking-ids';
import { TaskMutationStrategy } from '../../types';
import { convertDateToUTC, getLocationTimeZone } from '../../utils';
import { footerStyle, headerActions, headerStyle } from './create-task.styles';
import TaskForm from './task-form';
import { withPrefetchTaskById } from './with-prefetch-task-by-id';

interface CreateTaskProps {
  selectedTask: TaskCenterTypes.Task | null;
}

const CreateTask: FC<CreateTaskProps> = ({ selectedTask }) => {
  const alert = useAlert();
  const queryClient = useQueryClient();
  const { t } = useTranslation('task-center');

  const { taskMode, locationIds, locationsData, setTab, setSelectedTask, setTaskMode, setTaskId } = useTaskCenterStore([
    'taskMode',
    'setTaskMode',
    'locationIds',
    'locationsData',
    'setSelectedTask',
    'setTab',
    'setTaskId',
  ]);

  const isEditing = taskMode === 'edit' && !!selectedTask;

  const [dueDateHasTime, setDueDateHasTime] = useState(!!selectedTask?.dueDateHasTime);
  const { taskCreateMutation, taskUpdateMutation } = useTaskCenterMutations();
  const { formValues, formProps, haveValuesChanged, getUpdatedValues } = useGetFormDetails({
    locationIds,
    selectedTask: selectedTask,
    locationsData,
  });
  const { ConfirmationModal, modalProps, triggerProps } = useDeleteTask({ id: selectedTask?.id });
  const { setPatient, patient } = useTaskFormPatient(selectedTask?.patient?.id);
  const { removeQuery } = useRemoveQueryByKey();

  const showButtons = isEditing ? haveValuesChanged : true;

  const trackingIdSet = taskCenterTrackingIds[isEditing ? 'edit' : 'create'];
  const taskMetadata: CitationMetadata = selectedTask?.metadata ? JSON.parse(selectedTask.metadata) : undefined;

  const selectedLocation = formProps.getFieldProps('locationId').value;
  const { data: assignees, isFetching: fetchingPatients } = useAssigneeByLocation(
    [selectedLocation],
    taskMetadata?.source?.locationPhone
  );

  // @ts-expect-error form values are returned as string instead of task types. Will update with this ticket
  // https://linear.app/getweave/issue/BIZX-721/updated-taskcenter-create-task-form-types
  const getTaskMutation: () => TaskMutationStrategy = () => {
    const user = getUser();
    const taskValues = getUpdatedValues();
    const timezone = getLocationTimeZone(locationsData, formValues.locationId);

    if (taskMode === 'edit' && selectedTask?.id && user?.userID) {
      return {
        type: 'update',
        mutation: taskUpdateMutation,
        params: {
          id: selectedTask.id,
          updatedBy: user.userID,
          type: selectedTask.type,
          source: selectedTask.source,
          ...taskValues,
          ...(patient &&
            taskValues.patient && {
              patient: { id: patient.personId as string, name: PersonsV3.PersonHelpers.getFullName(patient) },
            }),
          ...(taskValues.dueDate && {
            dueDate: convertDateToUTC({ date: taskValues.dueDate, timezone, hasTime: dueDateHasTime }),
          }),
          ...(dueDateHasTime !== selectedTask?.dueDateHasTime && { dueDateHasTime }),
        },
        successText: t('Task updated successfully'),
        errorText: t('Unable to update task at this moment'),
      };
    } else if (taskMode === 'citation') {
      return {
        type: 'create',
        mutation: taskCreateMutation,
        params: {
          tasks: [
            {
              ...selectedTask,
              ...formValues,
              createdBy: user?.userID,
              ...(patient && {
                patient: { id: patient.personId as string, name: PersonsV3.PersonHelpers.getFullName(patient) },
              }),
              priority: formValues.priority || TaskCenterTypes.PriorityType.PRIORITY_UNKNOWN,
              ...(taskValues.dueDate && {
                dueDate: convertDateToUTC({ date: taskValues.dueDate, timezone, hasTime: dueDateHasTime }),
              }),
              dueDateHasTime,
            },
          ],
        },
        successText: t('Task created successfully'),
        errorText: t('Unable to create task at this moment'),
      };
    }

    return {
      type: 'create',
      mutation: taskCreateMutation,
      params: {
        tasks: [
          {
            ...taskValues,
            createdBy: user?.userID,
            ...(patient && {
              patient: { id: patient.personId as string, name: PersonsV3.PersonHelpers.getFullName(patient) },
            }),
            ...(taskValues.dueDate && {
              dueDate: convertDateToUTC({ date: taskValues.dueDate, timezone, hasTime: dueDateHasTime }),
            }),
            source: TaskCenterTypes.TaskSource.SOURCE_TASK_CENTER,
            priority: formValues.priority || TaskCenterTypes.PriorityType.PRIORITY_UNKNOWN,
            dueDateHasTime,
          },
        ],
      },
      successText: t('Task created successfully'),
      errorText: t('Unable to create task at this moment'),
    };
  };

  const saveTask = () => {
    formProps.validate();

    if (!formProps.isComplete) {
      return;
    }

    const config = getTaskMutation();
    const onError = () => {
      alert.error(config.errorText);
    };
    const onSuccess = () => {
      if (config.type === 'update') {
        taskEvents.emit('TASK_UPDATED', config.params);
      }
      alert.success(config.successText);
      queryClient.invalidateQueries(TaskCenterQueries.queryEndpointKeys.tasksList);
      setTab(TabsController[formProps.values.status as keyof typeof TabsController]);
      setTaskMode(null);
      setSelectedTask(null);
      setTaskId(null);

      formProps.reset();
      if (selectedTask?.id) {
        const { queryKey } = TaskCenterUtils.getTaskByIdQueryData(locationIds, selectedTask.id);
        removeQuery(queryKey);
      }
    };

    if (config.type === 'update') {
      config.mutation.mutate(config.params, { onError, onSuccess });
    } else {
      config.mutation.mutate(config.params, { onError, onSuccess });
    }
  };

  function clickBackHandler() {
    setTaskMode(null);
    setSelectedTask(null);
    setTaskId(null);
    formProps.reset();
  }

  return (
    <>
      <header css={headerStyle}>
        <Button
          iconName='caret-left'
          variant='secondary'
          size='small'
          onClick={clickBackHandler}
          disabled={taskCreateMutation.isLoading}
          trackingId={trackingIdSet.cancelProgressBackButton}
          label={t('Back')}
        />
        <PanelHeader.Title level={3}>{isEditing ? t('Task Details') : t('New Task')}</PanelHeader.Title>
        <div css={headerActions}>
          {isEditing && (
            <Button hoverLabel={t('Delete Task')} iconName='trash' variant='secondary' size='large' {...triggerProps} />
          )}
        </div>
      </header>
      <TaskForm
        formProps={formProps}
        selectedTask={selectedTask}
        taskMetadata={taskMetadata}
        trackingIdSet={trackingIdSet}
        patient={patient}
        setPatient={setPatient}
        isEditing={isEditing}
        selectedLocation={selectedLocation}
        assignees={assignees}
        fetchingPatients={fetchingPatients}
        formValues={formValues}
        dueDateHasTime={dueDateHasTime}
        setDueDateHasTime={setDueDateHasTime}
      />
      {showButtons && (
        <ButtonBar css={footerStyle}>
          <Button
            variant='secondary'
            onClick={() => {
              setTaskMode(null);
              setSelectedTask(null);
              setTaskId(null);
              formProps.reset();
            }}
            trackingId={trackingIdSet.cancelProgressButton}
            label={t('Cancel')}
          >
            {t('Cancel')}
          </Button>
          <Button
            loading={taskCreateMutation.isLoading}
            onClick={saveTask}
            trackingId={trackingIdSet.saveProgressButton}
            label={isEditing ? t('Save') : t('Create Task')}
          >
            {isEditing ? t('Save') : t('Create Task')}
          </Button>
        </ButtonBar>
      )}
      <ConfirmationModal {...modalProps} />
    </>
  );
};

export const CreateOrUpdateTask = withPrefetchTaskById(CreateTask);
